import { lazy } from 'react';

// project import
import Loadable from 'components/Loadable';
import CommonLayout from 'layout/CommonLayout';
import MainLayout from 'layout/MainLayout';
import ModifierPriceSetUp from 'pages/menu/price-setup/modifierPriceSetUp';
import AddEditSuggestiveItem from 'pages/suggestiveItem/addEditSuggestiveItem';
import AuthGuard from 'utils/route-guard/AuthGuard';

// pages routing
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const AccessDenied = Loadable(lazy(() => import('pages/maintenance/accessDenied.js')));

// render main pages
const SyncData = Loadable(lazy(() => import('pages/dashboard/syncData.js')));
const MenuList = Loadable(lazy(() => import('pages/menu/menuList.js')));
const CategoryList = Loadable(lazy(() => import('pages/menu/categoryList.js')));
const SystemUserList = Loadable(lazy(() => import('pages/admin/systemUserList.js')));
const SiteList = Loadable(lazy(() => import('pages/site/siteList.js')));
const SiteDetail = Loadable(lazy(() => import('pages/site/siteDetail.js')));
const KdsConfigure = Loadable(lazy(() => import('pages/site/kdsConfigure.js')));
const Rules = Loadable(lazy(() => import('pages/rule/rules.js')));
const BannerList = Loadable(lazy(() => import('pages/banner/bannerList.js')));
const AddEditRule = Loadable(lazy(() => import('pages/rule/addEditRule.js')));
const ModifierItemsList = Loadable(lazy(() => import('pages/menu/modifierItemsList.js')));
const ScreenSaverList = Loadable(lazy(() => import('pages/screen-saver/screenSaverList.js')));
const PushNotification = Loadable(lazy(() => import('pages/notification/notificationRule.js')));
const NotifyCustomerList = Loadable(lazy(() => import('pages/notification/notifyCustomerList.js')));
const DeviceList = Loadable(lazy(() => import('pages/devices/deviceList.js')));
const Receipt = Loadable(lazy(() => import('pages/receipt/receipt.js')));
const Kds = Loadable(lazy(() => import('pages/kds/addEditKds.js')));
const ScheduleAdjustments = Loadable(lazy(() => import('pages/site/scheduleAdjustments.js')));
const SurchargeList = Loadable(lazy(() => import('pages/site/surcharge-list.js')));
const PriceSetup = Loadable(lazy(() => import('pages/menu/price-setup/priceSetup.js')));
const MenuPricingSetUp = Loadable(lazy(() => import('pages/menu/price-setup/menuPricingSetUp.js')));
const ModifierMapping = Loadable(lazy(() => import('pages/menu/modifierMapping.js')));
const AddCombo = Loadable(lazy(() => import('pages/menu/addCombo.js')))
const ComboTemplates = Loadable(lazy(() => import('pages/combo/comboTemplates.js')));
const SetupTemplates = Loadable(lazy(() => import('pages/combo/setupTemplates.js')));
const PosIngredientList = Loadable(lazy(() => import("pages/inventory/posIngredient/posIngredientList.js")));
const SupplierList = Loadable(lazy(() => import("pages/inventory/supplier/supplierList.js")));
const IngredientList = Loadable(lazy(() => import("pages/inventory/ingredient/IngredientList.js")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: 'sync',
              element: <SyncData />
            },
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: 'users',
              element: <SystemUserList />
            },
            {
              path: 'sites',
              element: <SiteList />
            },
            {
              path: 'rules',
              element: <Rules />
            },
            {
              path: 'notification',
              element: <PushNotification />
            },
            {
              path: 'devices',
              element: <DeviceList />
            },
            {
              path: 'kds',
              element: <Kds />
            }
          ]
        },
        {
          path: 'mobile-application',
          children: [
            {
              path: 'banners',
              element: <BannerList />
            },
            {
              path: 'submenu',
              element: <CategoryList />
            },
            {
              path: 'menu',
              element: <MenuList />
            },
            {
              path: 'modifieritems',
              element: <ModifierItemsList />
            },
            {
              path: 'screenSavers',
              element: <ScreenSaverList />
            },
            {
              path: 'suggestiveitems',
              element: <AddEditSuggestiveItem />
            },
            {
              path: 'price-setup',
              element: <PriceSetup />
            },
            {
              path: 'set-item-price',
              element: <MenuPricingSetUp />
            },
            {
              path: 'set-modifier-price',
              element: <ModifierPriceSetUp />
            },
            {
              path: 'modifier-mapping',
              element: <ModifierMapping />
            },
            {
              path: 'templates',
              element: <ComboTemplates />,
            },
            {
              path: 'setup',
              element: <SetupTemplates />
            },
          ]
        },
        {
          path: "inventory",
          children: [
            {
              path: "pos-items",
              element: <PosIngredientList />,
            },
            {
              path: "suppliers",
              element: <SupplierList />,
            },
            {
              path: "components",
              element: <IngredientList />,
            },
          ],
        },
        {
          path: '/maintenance',
          element: <CommonLayout />,
          children: [
            {
              path: 'access-denied',
              element: <AccessDenied />
            },
            {
              path: '404',
              element: <MaintenanceError />
            },
            {
              path: '500',
              element: <MaintenanceError500 />
            },
            {
              path: 'under-construction',
              element: <MaintenanceUnderConstruction />
            }
          ]
        },
        {
          path: 'site/info/:siteId',
          element: <SiteDetail />
        },
        {
          path: 'operating-hours',
          element: <ScheduleAdjustments />
        },
        {
          path: 'kds-configure',
          element: <KdsConfigure />
        },
        {
          path: 'addEditRule/save/:conditionId?',
          element: <AddEditRule />
        },
        {
          path: 'execute/:notificationRuleId/:description',
          element: <NotifyCustomerList />
        },
        {
          path: 'admin/site/surcharge',
          element: <SurchargeList />
        },
        {
          path: 'mobile-application/addcombo',
          element: <AddCombo />
        }
      ]
    },
    {
      path: '/receipt/:orderId',
      element: <Receipt />
    },
    {
      path: '/receipt/:flavourId/:orderId',
      element: <Receipt />
    }
  ]
};

export default MainRoutes;
